import React, { useState, useCallback, useMemo } from 'react';
import {
  Plug,
  Settings,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Slack,
  Github,
  Cloud,
  BarChart,
  Trello, // Using Trello icon for Jira, as a direct Jira icon might not exist in Lucide
  CreditCard,
  Zap,
  Mail,
  RefreshCw,
} from 'lucide-react';

// --- Utility Functions & Mock Data ---

// Mocking a professional logging/notification system
const useNotification = () => {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState('info'); // info, success, danger

  const notify = (msg, msgType = 'info') => {
    setMessage(msg);
    setType(msgType);
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  };

  return { notify, message, visible, type };
};

// Map string names to Lucide icons
const IconMap = {
  Slack: Slack,
  Github: Github,
  Cloud: Cloud,
  BarChart: BarChart,
  Jira: Trello,
  Stripe: CreditCard,
  Mailchimp: Mail,
};

const MOCK_INTEGRATIONS = [
  {
    id: 'slack',
    name: 'Slack',
    icon: 'Slack',
    description: 'Receive real-time notifications about user activity, new tickets, and system alerts directly in your team channels.',
    status: 'Connected',
    configNeeded: false,
    color: 'text-green-600',
  },
  {
    id: 'github',
    name: 'GitHub',
    icon: 'Github',
    description: 'Sync development tasks, link commits to project issues, and manage code review requests.',
    status: 'Needs Configuration',
    configNeeded: true,
    color: 'text-yellow-600',
  },
  {
    id: 'salesforce',
    name: 'Salesforce CRM',
    icon: 'Cloud',
    description: 'Push contact data, lead status updates, and sales pipeline metrics to your CRM.',
    status: 'Not Configured',
    configNeeded: true,
    color: 'text-gray-500',
  },
  {
    id: 'google_analytics',
    name: 'Google Analytics',
    icon: 'BarChart',
    description: 'Track user page views, session duration, and referral sources to optimize performance.',
    status: 'Connected',
    configNeeded: false,
    color: 'text-green-600',
  },
  {
    id: 'jira',
    name: 'Jira',
    icon: 'Jira',
    description: 'Create and update tickets directly from the platform, keeping development and product teams aligned.',
    status: 'Not Configured',
    configNeeded: true,
    color: 'text-gray-500',
  },
  {
    id: 'stripe',
    name: 'Stripe',
    icon: 'Stripe',
    description: 'Process payments, manage subscriptions, and view transaction history securely.',
    status: 'Connected',
    configNeeded: true,
    color: 'text-green-600',
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    icon: 'Mailchimp',
    description: 'Sync user lists and automate marketing campaigns based on customer behavior.',
    status: 'Not Configured',
    configNeeded: true,
    color: 'text-gray-500',
  },
];

// --- Reusable UI Components ---

const ButtonPrimary = ({ onClick, children, className = '', disabled = false, icon: Icon, type = 'primary' }) => {
  let baseClasses = `px-3 py-1.5 text-xs font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2`;
  let colorClasses = '';

  switch (type) {
    case 'danger':
      colorClasses = 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-gray-300 disabled:text-gray-500';
      break;
    case 'secondary':
      colorClasses = 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-blue-500 disabled:text-gray-400';
      break;
    case 'success':
      colorClasses = 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-gray-300 disabled:text-gray-500';
      break;
    case 'primary':
    default:
      colorClasses = 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-gray-300 disabled:text-gray-500';
      break;
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${colorClasses} ${className}`}
    >
      {Icon && <Icon className="w-4 h-4 mr-1" />}
      {children}
    </button>
  );
};

// --- Integration Card Component ---

const IntegrationCard = ({ integration, onAction, notify }) => {
  const IconComponent = IconMap[integration.icon] || Plug;
  const statusColor = integration.color;

  let buttonText, ButtonIcon, buttonType, buttonAction;

  switch (integration.status) {
    case 'Connected':
      buttonText = integration.configNeeded ? 'Configure' : 'Disconnect';
      ButtonIcon = integration.configNeeded ? Settings : XCircle;
      buttonType = integration.configNeeded ? 'secondary' : 'danger';
      buttonAction = integration.configNeeded ? 'configure' : 'disconnect';
      break;
    case 'Needs Configuration':
      buttonText = 'Configure';
      ButtonIcon = Settings;
      buttonType = 'primary';
      buttonAction = 'configure';
      break;
    case 'Not Configured':
    default:
      buttonText = 'Connect';
      ButtonIcon = Zap;
      buttonType = 'success';
      buttonAction = 'connect';
      break;
  }
  
  // Custom status indicator component
  const StatusIndicator = ({ status, color }) => {
      let StatusIcon = AlertTriangle;
      if (status === 'Connected') {
          StatusIcon = CheckCircle;
      } else if (status === 'Needs Configuration') {
          StatusIcon = RefreshCw;
      }
      return (
          <span className={`flex items-center text-xs font-semibold ${color}`}>
              <StatusIcon className={`w-3 h-3 mr-1 ${color}`} />
              {status}
          </span>
      );
  };


  const handleAction = () => {
    if (buttonAction === 'configure') {
        notify(`Opening configuration for ${integration.name}...`, 'info');
        // In a real app, this would open a modal or redirect
    }
    onAction(integration.id, buttonAction);
  };

  return (
    <div className="flex flex-col bg-white p-6 rounded-xl shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl">
      {/* Header & Icon */}
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-full bg-blue-500/10 ${statusColor}`}>
          <IconComponent className="w-6 h-6" />
        </div>
        <StatusIndicator status={integration.status} color={statusColor} />
      </div>

      {/* Content */}
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{integration.name}</h3>
      <p className="text-sm text-gray-500 mb-4 flex-grow">{integration.description}</p>
      
      {/* Action */}
      <div className="mt-auto pt-4 border-t border-gray-100">
        <ButtonPrimary 
          onClick={handleAction} 
          icon={ButtonIcon}
          type={buttonType}
          className="w-full"
        >
          {buttonText}
        </ButtonPrimary>
      </div>
    </div>
  );
};


// --- Main Component ---

export default function IntegrationsManager() {
  const [integrations, setIntegrations] = useState(MOCK_INTEGRATIONS);
  const { notify, message, visible, type } = useNotification();

  // Notification color logic
  const notificationColor = useMemo(() => {
    if (type === 'success') return 'bg-green-600';
    if (type === 'danger') return 'bg-red-600';
    return 'bg-gray-800';
  }, [type]);

  // Handler for all connection actions
  const handleIntegrationAction = useCallback((id, action) => {
    let newStatus, newColor, newConfigNeeded;
    let successMessage;

    switch (action) {
      case 'connect':
        newStatus = 'Connected';
        newColor = 'text-green-600';
        newConfigNeeded = true; // Assume new connections often need initial setup
        successMessage = `Successfully connected to ${integrations.find(i => i.id === id)?.name}. Please configure settings.`;
        break;
      case 'disconnect':
        newStatus = 'Not Configured';
        newColor = 'text-gray-500';
        newConfigNeeded = true;
        successMessage = `Successfully disconnected from ${integrations.find(i => i.id === id)?.name}.`;
        break;
      case 'configure':
        // Configuration simulation: flips configNeeded to false if currently Connected
        const currentIntegration = integrations.find(i => i.id === id);
        if (currentIntegration.status === 'Connected') {
             newStatus = 'Connected';
             newColor = 'text-green-600';
             newConfigNeeded = false;
             successMessage = `${currentIntegration.name} configuration updated successfully.`;
        } else {
             // If trying to configure a non-connected integration (e.g., Salesforce)
             newStatus = 'Connected';
             newColor = 'text-green-600';
             newConfigNeeded = false;
             successMessage = `${currentIntegration.name} connected and configured successfully.`;
        }
        break;
      default:
        return;
    }

    setIntegrations(prev => prev.map(i => 
      i.id === id 
      ? { ...i, status: newStatus, color: newColor, configNeeded: newConfigNeeded }
      : i
    ));
    
    notify(successMessage, action === 'disconnect' ? 'danger' : 'success');
  }, [integrations, notify]);

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-xl font-bold text-gray-800 flex items-center">
          <Plug className="w-6 h-6 mr-3 text-blue-600" />
          Third-Party Integrations
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage connections and configurations for all external services.
        </p>
      </div>

      {/* Main Grid of Integrations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {integrations.map((integration) => (
          <IntegrationCard 
            key={integration.id} 
            integration={integration} 
            onAction={handleIntegrationAction}
            notify={notify}
          />
        ))}
        {/* Placeholder for new/upcoming integrations */}
        <div className="flex flex-col bg-gray-100 p-6 rounded-xl shadow-inner border-2 border-dashed border-gray-300 items-center justify-center text-center">
            <Zap className="w-8 h-8 text-gray-400 mb-3" />
            <h3 className="text-lg font-semibold text-gray-500 mb-1">More Coming Soon!</h3>
            <p className="text-sm text-gray-400">We are always adding new services to integrate with your workflow.</p>
        </div>
      </div>

      {/* Custom Notification Box (Instead of alert()) */}
      <div 
        className={`fixed bottom-4 right-4 p-3 rounded-lg shadow-xl text-xs font-medium transition-all duration-300 text-white 
                    ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'} ${notificationColor}`}
      >
        {message}
      </div>
    </div>
  );
}